/**
 * 공지사항 상세 페이지
 */

import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Badge, Button, Spinner } from 'react-bootstrap';
import AdminLayout from '../../components/layout/AdminLayout';
import { noticeService } from '../../services/noticeService';
import { Notice } from '../../types/notice';

const NoticeDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const noticeId = id ? parseInt(id, 10) : 0;

  const { data: notice, isLoading } = useQuery({
    queryKey: ['notice', noticeId],
    queryFn: () => noticeService.getNotice(noticeId),
    enabled: !!noticeId && noticeId > 0,
  });

  const getNoticeTypeBadge = (type: string) => {
    switch (type) {
      case 'urgent':
        return <Badge bg="danger">긴급</Badge>;
      case 'maintenance':
        return <Badge bg="warning">점검</Badge>;
      default:
        return <Badge bg="info">일반</Badge>;
    }
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <Spinner animation="border" variant="primary" />
        </div>
      </AdminLayout>
    );
  }

  if (!notice) {
    return (
      <AdminLayout>
        <Card>
          <Card.Body>
            <p>공지사항을 찾을 수 없습니다.</p>
            <Button variant="secondary" onClick={() => navigate('/admin/notices')}>
              목록으로
            </Button>
          </Card.Body>
        </Card>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 style={{ color: '#fff' }}>공지사항 상세</h1>
          <div>
            <Button variant="secondary" onClick={() => navigate('/admin/notices')} className="me-2">
              목록으로
            </Button>
            <Button variant="primary" onClick={() => navigate(`/admin/notices/${noticeId}/edit`)}>
              수정
            </Button>
          </div>
        </div>

        <Card>
          <Card.Body>
            <div className="mb-3">
              <h3>{notice.title.ko || notice.title.en || '-'}</h3>
              <div className="mt-2">
                {getNoticeTypeBadge(notice.notice_type)}
                {notice.is_main_visible && (
                  <Badge bg="primary" className="ms-2">메인 노출</Badge>
                )}
                <Badge bg={notice.status === 'active' ? 'success' : 'secondary'} className="ms-2">
                  {notice.status === 'active' ? '활성' : '비활성'}
                </Badge>
              </div>
            </div>

            <div className="mb-3">
              <strong>우선순위:</strong> {notice.priority}
            </div>

            {notice.start_date && (
              <div className="mb-3">
                <strong>시작일:</strong> {new Date(notice.start_date).toLocaleString('ko-KR')}
              </div>
            )}

            {notice.end_date && (
              <div className="mb-3">
                <strong>종료일:</strong> {new Date(notice.end_date).toLocaleString('ko-KR')}
              </div>
            )}

            <div className="mb-3">
              <strong>등록일:</strong> {new Date(notice.created_at).toLocaleString('ko-KR')}
            </div>

            {notice.updated_at && (
              <div className="mb-3">
                <strong>수정일:</strong> {new Date(notice.updated_at).toLocaleString('ko-KR')}
              </div>
            )}

            <hr />

            <div className="mb-3">
              <h5>내용 (한국어)</h5>
              <div style={{ 
                padding: '15px', 
                backgroundColor: '#2E2E3A', 
                borderRadius: '5px',
                color: '#ccc',
                whiteSpace: 'pre-wrap'
              }}>
                {notice.content.ko || '-'}
              </div>
            </div>

            {notice.content.en && (
              <div className="mb-3">
                <h5>내용 (영어)</h5>
                <div style={{ 
                  padding: '15px', 
                  backgroundColor: '#2E2E3A', 
                  borderRadius: '5px',
                  color: '#ccc',
                  whiteSpace: 'pre-wrap'
                }}>
                  {notice.content.en}
                </div>
              </div>
            )}

            {notice.content.th && (
              <div className="mb-3">
                <h5>내용 (태국어)</h5>
                <div style={{ 
                  padding: '15px', 
                  backgroundColor: '#2E2E3A', 
                  borderRadius: '5px',
                  color: '#ccc',
                  whiteSpace: 'pre-wrap'
                }}>
                  {notice.content.th}
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default NoticeDetail;

